/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  getListingProductIdFromState,
  listListingVariantIdsForChannel,
} from './helpers/entityReadHelpers';
import { removeListing, removeListingVariant } from './helpers/entityAdapter';
import pruneDeletedListingRows from './helpers/pruneDeletedListingRows';
import { MASTER_SELECTION_CONTEXT } from './helpers/selectionContext';
import { toMasterProductEntry } from './helpers/toSelectionEntry';
import { setSelectedChannelByProduct } from './helpers/uiAdapter';

const selectMasterIfSelectionCleared = (state, productId, source) => {
  if (state.ui?.selection?.keys?.length) return;

  const product = (state.items || []).find((item) => String(item._id) === String(productId));
  const entry = toMasterProductEntry(product || { _id: productId });
  const key = entry.key;
  if (!state.ui || !key) return;

  state.ui.selection = {
    keys: [key],
    byKey: { [key]: entry },
    lastClickedKey: key,
    isComplete: false,
    context: MASTER_SELECTION_CONTEXT,
    source: source || 'search',
  };
};

export const deleteProductLiveSnapshot = createAsyncThunk(
  'products/deleteProductLiveSnapshot',
  async ({ productId, channelId }, { getState, extra: { Logger, Toast, sdk } }) => {
    try {
      const listingProductId = getListingProductIdFromState(getState().products, productId, channelId);

      if (!listingProductId) {
        throw new Error(`Listing not found for channel ${channelId}`);
      }

      await sdk.products.deleteListing(listingProductId);

      return { productId, channelId };
    } catch (error) {
      Toast(
        'Listing is not removed. Refresh and try again',
        { autoClose: false, type: 'error' },
      );
      Logger.error(new Error('Error removing live listing'), { error }, [
        'RemoveLiveListingFailed',
        { errorMessage: 'Listing is not removed on the backend.' },
      ]);
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(deleteProductLiveSnapshot.pending, (state) => {
      state.isBusy = true;
      state.error = null;
    })
    .addCase(deleteProductLiveSnapshot.fulfilled, (state, { payload }) => {
      const { productId, channelId } = payload;
      state.isBusy = false;
      const previousSource = state.ui?.selection?.source;

      const listingProductId = getListingProductIdFromState(state, productId, channelId);
      const listingVariantIds = listListingVariantIdsForChannel(state, productId, channelId);

      listingVariantIds.forEach((listingVariantId) => {
        removeListingVariant(state, listingVariantId);
      });
      if (listingProductId) {
        removeListing(state, listingProductId);
      }
      pruneDeletedListingRows(state, {
        listingIds: listingProductId ? [listingProductId] : [],
        variantIds: listingVariantIds,
      });
      setSelectedChannelByProduct(state, productId, 'master');
      selectMasterIfSelectionCleared(state, productId, previousSource);
    })
    .addCase(deleteProductLiveSnapshot.rejected, (state, { error }) => {
      state.isBusy = false;
      state.error = error?.message || 'Cannot remove live listing';
    });
};
